import React, { useState } from "react";
import { motion } from "framer-motion";
import { ExternalLink, Car } from "lucide-react";
import { useT } from "@/lib/i18n";

/**
 * Local guide: every notable place near the cottage, grouped by the kind of
 * day out a guest is planning. Text-only by design: these are third-party
 * attractions, so we link to their own sites rather than reusing their photos.
 */
export const LocalGuide = () => {
  const { t } = useT();
  const guide = t.localGuide;
  const [active, setActive] = useState("all");
  if (!guide) return null;
  const categories = guide.categories;

  const visible =
    active === "all" ? guide.places : guide.places.filter((p) => p.category === active);

  return (
    <div className="mt-14">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="kicker">{guide.kicker}</div>
          <h3 className="mt-1 font-display text-2xl sm:text-3xl font-semibold text-foreground">
            {guide.title}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground max-w-md">{guide.subtitle}</p>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label={guide.title}>
        <button
          type="button"
          role="tab"
          aria-selected={active === "all"}
          onClick={() => setActive("all")}
          className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
            active === "all"
              ? "border-transparent text-primary-foreground"
              : "border-border bg-card text-foreground hover:bg-secondary/60"
          }`}
          style={active === "all" ? { background: "hsl(var(--pine))" } : undefined}
          data-testid="guide-filter-all"
        >
          {guide.allLabel}
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            type="button"
            role="tab"
            aria-selected={active === c.id}
            onClick={() => setActive(c.id)}
            className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${
              active === c.id
                ? "border-transparent text-primary-foreground"
                : "border-border bg-card text-foreground hover:bg-secondary/60"
            }`}
            style={active === c.id ? { background: "hsl(var(--pine))" } : undefined}
            data-testid={`guide-filter-${c.id}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="guide-list">
        {visible.map((p, idx) => (
          <motion.li
            key={p.name}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.35, ease: "easeOut", delay: Math.min(idx, 6) * 0.04 }}
            className="rounded-xl border border-border bg-card p-4 shadow-soft"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {p.href ? (
                    <a
                      href={p.href}
                      target="_blank"
                      rel="noreferrer"
                      className="font-medium text-foreground link-underline inline-flex items-center gap-1"
                    >
                      {p.name}
                      <ExternalLink className="h-3 w-3 text-muted-foreground" />
                    </a>
                  ) : (
                    <span className="font-medium text-foreground">{p.name}</span>
                  )}
                </div>
                <p className="mt-1 text-sm text-foreground/75 leading-relaxed">{p.body}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-1 rounded-full bg-secondary/70 px-2.5 py-1 text-xs font-medium text-foreground tabular-nums">
                <Car className="h-3 w-3" />
                {p.distance}
              </span>
            </div>
          </motion.li>
        ))}
      </ul>

      <p className="mt-4 text-xs text-muted-foreground">{guide.disclaimer}</p>
      {guide.moreHref && (
        <p className="mt-3">
          <a href={guide.moreHref} className="text-sm font-medium text-foreground link-underline" data-testid="guide-more-link">
            {guide.moreLabel}
          </a>
        </p>
      )}
    </div>
  );
};
